import dayjs from 'dayjs'
import styled from 'styled-components'

import { COLORS } from '../commons/Colors'
import LoadingComponent from '../commons/components/LoadingComponent'
import MainHeaderComponent from '../commons/components/MainHeaderComponent'
import SnackBarComponent from '../commons/components/SnackBarComponent'
import SwitchButtonComponent from '../commons/components/SwitchButtonComponent'
import { SnackbarConfigTypes } from '../commons/utils/Types'
import { FeatureFlag, FeatureFlagItem } from './API/FeatureFlagsAPI'
import FeatureFlagFormModal from './FeatureFlagFormModal'
import { getFlagInfo } from './flagCatalog'

type Props = {
    flags: FeatureFlagItem[]
    isLoading: boolean
    snackbarConfig: SnackbarConfigTypes
    flagToEdit: FeatureFlagItem | null
    isFormOpen: boolean
    onCloseSnackbar: () => void
    onTogglePlatform: (flag: FeatureFlagItem, platform: 'app' | 'web') => void
    onEdit: (flag: FeatureFlagItem) => void
    onFormClose: () => void
    onSave: (data: FeatureFlag) => Promise<void>
}

const isPlatformEnabled = (
    flag: FeatureFlagItem,
    platform: 'app' | 'web'
): boolean => flag[platform]?.enabled ?? flag.enabled ?? true

// Flags fora do catálogo: assume que a plataforma se aplica (mantém comportamento antigo).
const platformApplies = (
    flag: FeatureFlagItem,
    platform: 'app' | 'web'
): boolean => getFlagInfo(flag.documentId)?.[platform].applies ?? true

function renderDescription(flag: FeatureFlagItem) {
    const info = getFlagInfo(flag.documentId)
    if (!info) {
        return <span>{flag.description ?? '—'}</span>
    }
    return (
        <FlagDescription>
            <Summary>{info.summary}</Summary>
            <PlatformLine>
                <PlatformLabel>APP:</PlatformLabel>{' '}
                {info.app.applies ? info.app.effect : 'não se aplica'}
            </PlatformLine>
            <PlatformLine>
                <PlatformLabel>WEB:</PlatformLabel>{' '}
                {info.web.applies ? info.web.effect : 'não se aplica'}
            </PlatformLine>
        </FlagDescription>
    )
}

function renderPlatformToggle(
    flag: FeatureFlagItem,
    platform: 'app' | 'web',
    onTogglePlatform: (flag: FeatureFlagItem, platform: 'app' | 'web') => void
) {
    if (!platformApplies(flag, platform)) {
        return (
            <NotApplicable>
                <SwitchButtonComponent
                    active={false}
                    disabled
                    hideLabel
                    onClick={() => undefined}
                    testid={`toggle-${platform}-${flag.documentId}`}
                />
                <NotApplicableText>não se aplica</NotApplicableText>
            </NotApplicable>
        )
    }
    return (
        <SwitchButtonComponent
            active={isPlatformEnabled(flag, platform)}
            onClick={() => onTogglePlatform(flag, platform)}
            activeText="Habilitado"
            inactiveText="Desabilitado"
            testid={`toggle-${platform}-${flag.documentId}`}
        />
    )
}

export default function FeatureFlagsView({
    flags,
    isLoading,
    snackbarConfig,
    flagToEdit,
    isFormOpen,
    onCloseSnackbar,
    onTogglePlatform,
    onEdit,
    onFormClose,
    onSave,
}: Props) {
    return (
        <PageContainer>
            <MainHeaderComponent />
            <Content>
                <PageTitle>Feature Flags</PageTitle>

                {isLoading && <LoadingComponent />}
                {!isLoading && flags.length === 0 && (
                    <EmptyMessage>
                        Nenhuma feature flag configurada.
                    </EmptyMessage>
                )}
                {!isLoading && flags.length > 0 && (
                    <Table>
                        <thead>
                            <tr>
                                <Th>Nome</Th>
                                <Th>Descrição</Th>
                                <Th>APP</Th>
                                <Th>WEB</Th>
                                <Th>Última alteração</Th>
                                <Th>Ações</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {flags.map((flag) => (
                                <tr key={flag.documentId}>
                                    <Td>
                                        <FlagName>{flag.documentId}</FlagName>
                                    </Td>
                                    <Td>{renderDescription(flag)}</Td>
                                    <Td>
                                        {renderPlatformToggle(
                                            flag,
                                            'app',
                                            onTogglePlatform
                                        )}
                                    </Td>
                                    <Td>
                                        {renderPlatformToggle(
                                            flag,
                                            'web',
                                            onTogglePlatform
                                        )}
                                    </Td>
                                    <Td>
                                        {flag.updatedAt
                                            ? dayjs(flag.updatedAt).format(
                                                  'DD/MM/YYYY HH:mm'
                                              )
                                            : '—'}
                                    </Td>
                                    <Td>
                                        <ActionButton
                                            onClick={() => onEdit(flag)}
                                        >
                                            Configurar
                                        </ActionButton>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Content>

            <FeatureFlagFormModal
                isOpen={isFormOpen}
                flag={flagToEdit}
                onClose={onFormClose}
                onSave={onSave}
            />

            <SnackBarComponent
                handleClose={onCloseSnackbar}
                isVisible={snackbarConfig.isVisible}
                message={snackbarConfig.message}
                severity={snackbarConfig.status}
            />
        </PageContainer>
    )
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`

const Content = styled.div`
    margin: 30px;
    background-color: ${COLORS.primaryWhite};
    border: solid 1px ${COLORS.borderGray};
    border-radius: 3px;
    padding: 25px;
`

const PageTitle = styled.h1`
    font-size: 18px;
    font-family: Roboto;
    color: ${COLORS.primaryGray};
    font-weight: bold;
    margin: 0 0 24px 0;
`

const EmptyMessage = styled.p`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 14px;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: Roboto;
    font-size: 14px;
`

const Th = styled.th`
    text-align: left;
    padding: 10px 12px;
    border-bottom: 2px solid ${COLORS.borderGray};
    color: ${COLORS.primaryGray};
    font-weight: bold;
`

const Td = styled.td`
    padding: 12px;
    border-bottom: 1px solid ${COLORS.borderGray};
    color: ${COLORS.primaryGray};
    vertical-align: middle;
`

const FlagName = styled.code`
    font-size: 12px;
    background: #f4f4f4;
    padding: 2px 6px;
    border-radius: 4px;
`

const FlagDescription = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 360px;
`

const Summary = styled.span`
    color: ${COLORS.primaryGray};
`

const PlatformLine = styled.span`
    font-size: 12px;
    color: ${COLORS.primaryGray};
`

const PlatformLabel = styled.strong`
    font-weight: bold;
`

const NotApplicable = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    opacity: 0.6;
`

const NotApplicableText = styled.span`
    font-size: 11px;
    font-style: italic;
    color: ${COLORS.primaryGray};
`

const ActionButton = styled.button`
    background: none;
    border: 1px solid ${COLORS.borderGray};
    color: ${COLORS.primaryGray};
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    font-family: Roboto;

    &:hover {
        background: #f5f5f5;
    }
`
